import React, { useContext, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation, useNavigate } from "react-router-dom";
import OtherAuth from "../components/OtherAuth.jsx";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import MainHeader from "../components/MainHeader.jsx";
import useAxiosRequests from "../hooks/useAxiosRequest.js";
import ExternalRoutes from "../config/ExternalRoutes.js";
import AuthContext from "../context/AuthContext.js";
import { MessageContext } from "../context/MessageContext.js";
import { jwtDecode } from 'jwt-decode';
import { CircularProgress } from "@mui/material";

export default function Login() {
    const navigate = useNavigate();
    const { login, user } = useContext(AuthContext);
    const { showSuccess, showError } = useContext(MessageContext);
    const location = useLocation();
    const queryParams = new URLSearchParams(window.location.search);
    const status = queryParams.get('status');
    const message = queryParams.get('message');
    const token = queryParams.get('token');
    const [loading, setLoading] = useState(false);

    const decodeUtf8Base64 = (base64String) => {
        try {
            const binaryString = atob(base64String);
            const bytes = new Uint8Array(binaryString.length);

            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            return new TextDecoder('utf-8').decode(bytes);
        } catch (error) {
            return null;
        }
    };
    const decodeBase64 = (base64String) => {
        try {
            const decodedString = atob(base64String);
            return JSON.parse(decodedString);
        } catch (error) {
            return null;
        }
    };

    const decodedMessage = message ? decodeUtf8Base64(message) : null;

    useEffect(() => {
        if (user && user.role.includes("ROLE_ADMIN")) {
            navigate("/contab-admin");
        } else if (user) {
            navigate('/');
        }
        if (status === 'success') {
            showSuccess(decodedMessage);
        }
        if (status === 'expired') {
            showError(decodedMessage);
        }
        if (status === 'error') {
            showError(decodedMessage);
        }
        // Ajoute location.pathname et user comme dépendances
    }, [user, location.pathname]);


    const schema = Yup.object().shape({
        email: Yup.string()
            .email("Adresse e-mail invalide")
            .required("L'e-mail est requis"),
        password: Yup.string()
            .min(6, "Le mot de passe doit contenir au moins 6 caractères")
            .required("Le mot de passe est requis"),
    });

    const [showPassword, setShowPassword] = React.useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const { sendRequest } = useAxiosRequests();

    const handleLogin = async (data, { setSubmitting }) => {
        setLoading(true);
        try {
            const response = await sendRequest({
                method: "POST",
                url: ExternalRoutes.authConfig.login,
                body: data,
            });

            if (response.status === "success" && response.message) {
                showSuccess(response.message);
                const userData = {
                    token: response.data.token,
                    role: response.data.userData.role,
                    userInfo: response.data.userData,
                };
                login(userData);
                if (response.data.userData.role.includes("ROLE_ADMIN")) {
                    navigate("/contab-admin");
                } else {
                    navigate("/");
                }
            } else if (response.status !== "success" && response.message) {
                showError(response.message);
            } else {
                showError("Une erreur inattendue est survenue.");
            }

            if (!response.data || !response.data.token || !response.data.userData) {
                return;
            }

        } catch (error) {
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    // Fonction de connexion automatique avec un token
    const handleAutoLogin = async (token) => {
        setLoading(true);
        try {
            const decodedToken = decodeBase64(token);

            if (!decodedToken) {
                throw new Error("Token invalide ou malformé");
            }

            const user = decodedToken.userData;
            const userData = {
                token: decodedToken.token,
                role: user.roles,
                userInfo: {
                    ...user
                },
            };

            login(userData);
            navigate("/");
        } catch (error) {
            showError("Erreur lors de la connexion automatique.");
        } finally {
            setLoading(false);
        }
    };

    // Appel automatique si un token est présent dans l'URL
    useEffect(() => {
        if (token) {
            handleAutoLogin(token);
        }
    }, [token]);

    return (
        <>
            <MainHeader />
            <div className="container">
                <div className="">
                    <div className="kl-login-page">
                        <h1>J'ai un compte Comptaride</h1>
                        <span>Explorez votre espace personnel</span>
                    </div>
                    <div className="kl-other-login">
                        <OtherAuth />
                    </div>

                    <div className="kl-hr-login">
                        <hr />
                    </div>



                    <Formik
                        initialValues={{ email: "", password: "" }}
                        validationSchema={schema}
                        onSubmit={handleLogin}
                    >
                        {({
                            handleSubmit,
                            handleChange,
                            handleBlur,
                            values,
                            errors,
                            touched,
                            isValid,
                            isSubmitting,
                        }) => (
                            <form
                                noValidate
                                autoComplete="off"
                                className=""
                                onSubmit={handleSubmit}
                            >
                                {/* Champ Email */}
                                <div className="kl-form-control-login">
                                    <div className="mb-4">
                                        <div className="kl-input-container">
                                            <div className="kl-fa-icon">
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M14.167 17.2082H5.83366C4.40774 17.2082 3.35103 16.7997 2.65075 16.0994C1.95047 15.3991 1.54199 14.3424 1.54199 12.9165V7.08317C1.54199 5.65725 1.95047 4.60055 2.65075 3.90027C3.35103 3.19999 4.40774 2.7915 5.83366 2.7915H14.167C15.5929 2.7915 16.6496 3.19999 17.3499 3.90027C18.0502 4.60055 18.4587 5.65725 18.4587 7.08317V12.9165C18.4587 14.3424 18.0502 15.3991 17.3499 16.0994C16.6496 16.7997 15.5929 17.2082 14.167 17.2082ZM5.83366 3.0415C4.5685 3.0415 3.53163 3.34892 2.81552 4.06503C2.09941 4.78115 1.79199 5.81802 1.79199 7.08317V12.9165C1.79199 14.1817 2.09941 15.2185 2.81552 15.9346C3.53164 16.6508 4.5685 16.9582 5.83366 16.9582H14.167C15.4321 16.9582 16.469 16.6508 17.1851 15.9346C17.9012 15.2185 18.2087 14.1817 18.2087 12.9165V7.08317C18.2087 5.81801 17.9012 4.78115 17.1851 4.06503C16.469 3.34892 15.4321 3.0415 14.167 3.0415H5.83366Z" fill="#292D32" stroke="#9CA3AF" />
                                                    <path d="M8.36595 9.67913L8.36597 9.67911L8.36202 9.67596L5.75588 7.59438C5.69372 7.54298 5.69609 7.46498 5.7297 7.4236C5.78109 7.36036 5.85974 7.36256 5.90135 7.39637L5.90134 7.39638L5.90461 7.39899L8.512 9.48158C8.51216 9.48171 8.51232 9.48184 8.51248 9.48196C8.93375 9.81987 9.47734 9.97289 9.99581 9.97289C10.5143 9.97289 11.0579 9.81987 11.4791 9.48196C11.4793 9.48184 11.4795 9.48171 11.4796 9.48158L14.087 7.39899L14.087 7.399L14.0903 7.39637C14.1445 7.35231 14.2193 7.36783 14.2514 7.41024L14.2566 7.41701L14.2619 7.4236C14.306 7.47783 14.2905 7.55264 14.2481 7.58476L14.248 7.58463L14.2379 7.59263L11.6296 9.67596L11.6296 9.6759L11.6227 9.68155C11.1932 10.0372 10.6033 10.225 9.99998 10.225C9.39752 10.225 8.80565 10.0377 8.36595 9.67913Z" fill="#292D32" stroke="#9CA3AF" />
                                                </svg>
                                            </div>
                                            <Field
                                                as={Form.Control}
                                                type="email"
                                                name="email"
                                                className={`kl-input-login ${touched.email && errors.email ? "is-invalid" : ""
                                                    }`}
                                                placeholder="Votre email..."
                                                size="lg"
                                                onBlur={handleBlur}
                                            />
                                        </div>
                                        <ErrorMessage
                                            name="email"
                                            component="small"
                                            className="text-danger mb-2"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <div className="kl-input-container">
                                            <div className="kl-fa-icon">
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M5 8.33317V6.6665C5 3.90817 5.83333 1.6665 10 1.6665C14.1667 1.6665 15 3.90817 15 6.6665V8.33317" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M10.0003 15.4167C11.1509 15.4167 12.0837 14.4839 12.0837 13.3333C12.0837 12.1827 11.1509 11.25 10.0003 11.25C8.84973 11.25 7.91699 12.1827 7.91699 13.3333C7.91699 14.4839 8.84973 15.4167 10.0003 15.4167Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M14.167 18.3335H5.83366C2.50033 18.3335 1.66699 17.5002 1.66699 14.1668V12.5002C1.66699 9.16683 2.50033 8.3335 5.83366 8.3335H14.167C17.5003 8.3335 18.3337 9.16683 18.3337 12.5002V14.1668C18.3337 17.5002 17.5003 18.3335 14.167 18.3335Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                            <Field
                                                as={Form.Control}
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                className={`kl-input-login ${touched.password && errors.password ? "is-invalid" : ""
                                                    }`}
                                                placeholder="Mot de passe"
                                                size="lg"
                                                onBlur={handleBlur}
                                            />
                                            <FontAwesomeIcon
                                                icon={showPassword ? faEye : faEyeSlash}
                                                className="kl-eye-icon-password"
                                                onClick={togglePasswordVisibility}
                                            />
                                        </div>
                                        <ErrorMessage
                                            name="password"
                                            component="small"
                                            className="text-danger mb-2"
                                        />
                                    </div>
                                    <div className="kl-reset-password text-end">
                                        <Link to="/forgot-password">Mot de passe oublié?</Link>
                                    </div>
                                </div>
                                <div className="kl-button-submit kl-button-login">
                                    <Button
                                        type="submit"
                                        variant={isValid ? "primary" : "secondary"}
                                        size="lg"
                                        disabled={!isValid || loading}
                                    >
                                        {loading ?
                                            <>
                                                <CircularProgress size={15} color="inherit" />
                                                <span className="mx-2">Connexion en cours...</span>
                                            </> : "Se connecter"}
                                        {/* {isSubmitting ? "Connexion en cours..." : "Se connecter"} */}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>


                <div className="kl-signup">
                    <span>
                        Vous n'avez pas de compte?{" "}
                        <Link to="/sign-up" className="kl-link-page">
                            S'inscrire?
                        </Link>
                    </span>
                </div>
            </div>
        </>
    );
}